import {
  collection,
  doc,
  query,
  where,
  orderBy,
  limit,
  getDocs,
  setDoc,
  addDoc,
  getDoc,
  onSnapshot,
  updateDoc
} from "firebase/firestore";
/**
 * Run a one-time Firestore query based on JSON config.
 * 
 * @param {object} config JSON configuration object
 * @returns {Promise<any>} Data from Firestore
 */
export async function queryFirestore(...inputs) {
  if (inputs.length < 5){
    console.log("queryFirestore_error_inputs_missing")
  }
  const config = inputs[0]
  const onResult = inputs[1]

  const { type = "collection", path, filters = [], options = {} } = config;

  if (!window.db || !path) throw new Error("Missing db or path");

  if (type === "document") {
    const docRef = doc(window.db, ...path);
    const docSnap = await getDoc(docRef);
    onResult(docSnap.exists() ? docSnap.data() : null);
  }

  if (type === "collection") {
    console.log(window.db);
    let colRef = collection(window.db, ...path);
    let q = colRef;

    if (filters.length > 0 || options.orderBy || options.limit) {
      const clauses = [];

      for (const f of filters) {
        clauses.push(where(f.field, f.op, f.value));
      }

      if (options.orderBy) {
        clauses.push(orderBy(options.orderBy.field, options.orderBy.direction || "asc"));
      }

      if (options.limit) {
        clauses.push(limit(options.limit));
      }

      q = query(colRef, ...clauses);
    }

    const querySnap = await getDocs(q);
    onResult(querySnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
  }

  if(type!='collection' && type!='document'){
    throw new Error("Invalid type. Use 'collection' or 'document'");
  }

}

/**
 * Set up a Firestore listener using config and callback.
 * 
 * @param {object} config Firestore query config
 * @param {function} onUpdate Callback to run on snapshot updates
 * @returns {function} Unsubscribe function
 */
export function listenFirestore(...inputs) {
  if (inputs.length < 5){
    console.log("listenFirestore_error_inputs_missing")
  }
  const config = inputs[0]
  const onUpdate = inputs[1]

  const { type = "collection", path, filters = [], options = {} } = config;

  if (!window.db || !path) throw new Error("Missing db or path");

  if (type === "document") {
    const docRef = doc(window.db, ...path);
    const unsub = onSnapshot(docRef, (docSnap) => {
      onUpdate(docSnap.exists() ? docSnap.data() : null);
    });
    return unsub;
  }

  if (type === "collection") {
    let colRef = collection(window.db, ...path);
    let q = colRef;

    if (filters.length > 0 || options.orderBy || options.limit) {
      const clauses = [];

      for (const f of filters) {
        clauses.push(where(f.field, f.op, f.value));
      }

      if (options.orderBy) {
        clauses.push(orderBy(options.orderBy.field, options.orderBy.direction || "asc"));
      }

      if (options.limit) {
        clauses.push(limit(options.limit));
      }

      q = query(colRef, ...clauses);
    }

    const unsub = onSnapshot(q, (querySnap) => {
      const docs = querySnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      onUpdate(docs);
    });

    return unsub;
  }

  if(type!='collection' && type!='document'){
    throw new Error("Invalid type. Use 'collection' or 'document'");
  }
}

/**
 * Updates Firestore based on a JSON config.
 *
 * @param {object} config
 * @param {"set"|"add"|"update"} config.mode - Type of write operation
 * @param {Array<string>} config.path - Path to collection or document
 * @param {object} config.data - The data to write
 * @param {boolean} [config.merge] - Optional for `set`, whether to merge
 * @returns {Promise<object>} - Metadata about the operation
 */
export async function updateFirestore(...inputs) {
  if (inputs.length < 5){
    console.log("updateFirestore_error_inputs_missing")
  }
  const config = inputs[0]
  const onResult = inputs[1]

  const { mode, path, data, merge = false } = config;

  if (!window.db || !Array.isArray(path) || path.length === 0 || !data) {
    throw new Error("Missing required fields (db, path, or data)");
  }

  if (mode === "add") {
    // Add to a collection with auto-generated ID
    const colRef = collection(window.db, ...path);
    const docRef = await addDoc(colRef, data);
    onResult({ id: docRef.id, path: docRef.path });
  }

  if (mode === "set") {
    // Set document (overwrite or merge)
    if (path.length % 2 === 0) {
      const docRef = doc(window.db, ...path);
      await setDoc(docRef, data, { merge });
      onResult({ id: docRef.id, path: docRef.path });
    } else {
      throw new Error("Invalid document path (must have even segments)");
    }
  }

  if (mode === "update") {
    // Update only specified fields
    if (path.length % 2 === 0) {
      const docRef = doc(window.db, ...path);
      await updateDoc(docRef, data);
      onResult({ id: docRef.id, path: docRef.path });
    } else {
      throw new Error("Invalid document path (must have even segments)");
    }
  }

  if(!['add', 'set', 'update'].includes(mode)){
    throw new Error(`Unsupported mode: ${mode}`);
  }
}
